import axios, { AxiosError } from 'axios';
import {
  HubConnectionBuilder,
  LogLevel,
  type HubConnection,
  type IHttpConnectionOptions
} from '@microsoft/signalr';
import { config } from '@/config';
import { getTenantRequestHeaders } from '@/feature/auth/utils/tenantStorage';

export interface RealtimeLatencyThresholds {
  excellentMs: number;
  goodMs: number;
  poorMs: number;
}

export interface RealtimeConnectionInfo {
  connectionId?: string;
  tenantId?: string;
  userId?: string;
  serverTime?: string;
  hubUrl?: string;
  pingIntervalMs: number;
  latencyThresholds: RealtimeLatencyThresholds;
}

export interface RealtimeNotificationPreferences {
  pushNotifications: boolean;
  newOrderNotification: boolean;
  commentNotification: boolean;
  statusNotification: boolean;
  reminderNotification: boolean;
}

export interface RealtimeAppNotificationsPage {
  data: unknown[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
}

export const defaultRealtimeNotificationPreferences: RealtimeNotificationPreferences =
  {
    pushNotifications: true,
    newOrderNotification: true,
    commentNotification: true,
    statusNotification: true,
    reminderNotification: true
  };

export function isUnauthorizedRealtimeError(error: unknown) {
  return error instanceof AxiosError && error.response?.status === 401;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function unwrapApiValue(value: unknown): unknown {
  if (!isRecord(value)) return value;

  return value.data ?? value.value ?? value;
}

function readStringValue(
  value: Record<string, unknown>,
  keys: string[]
): string | undefined {
  for (const key of keys) {
    const item = value[key];

    if (typeof item === 'string' && item.trim()) {
      return item.trim();
    }

    if (typeof item === 'number' && Number.isFinite(item)) {
      return String(item);
    }
  }

  return undefined;
}

function readNumberValue(
  value: Record<string, unknown>,
  keys: string[]
): number | undefined {
  for (const key of keys) {
    const item = value[key];

    if (typeof item === 'number' && Number.isFinite(item)) {
      return item;
    }

    if (typeof item === 'string') {
      const parsed = Number(item);

      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }

  return undefined;
}

function readRecordValue(
  value: Record<string, unknown>,
  keys: string[]
): Record<string, unknown> | undefined {
  for (const key of keys) {
    const item = value[key];

    if (isRecord(item)) {
      return item;
    }
  }

  return undefined;
}

function normalizeApiBaseUrl() {
  return config.apiUrl.replace(/\/+$/, '');
}

function normalizeHubUrl(value?: string) {
  const hubUrl = value?.trim();

  if (!hubUrl) {
    return `${normalizeApiBaseUrl()}/notifications`;
  }

  if (/^https?:\/\//i.test(hubUrl)) {
    return hubUrl;
  }

  return `${normalizeApiBaseUrl()}/${hubUrl.replace(/^\/+/, '')}`;
}

function getSignalRTenantHeaders() {
  return Object.fromEntries(
    Object.entries(getTenantRequestHeaders()).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string'
    )
  );
}

function normalizeLatencyThresholds(
  value: Record<string, unknown>
): RealtimeLatencyThresholds {
  const thresholds =
    readRecordValue(value, [
      'latencyThresholds',
      'qualityThresholds',
      'thresholds'
    ]) ?? value;

  return {
    excellentMs:
      readNumberValue(thresholds, [
        'excellentMs',
        'excellent',
        'excellentMaxMs',
        'excellentLatencyMs',
        'excellentLatencyThresholdMs'
      ]) ?? 150,
    goodMs:
      readNumberValue(thresholds, [
        'goodMs',
        'good',
        'goodMaxMs',
        'goodLatencyMs',
        'goodLatencyThresholdMs'
      ]) ?? 350,
    poorMs:
      readNumberValue(thresholds, [
        'poorMs',
        'poor',
        'poorMaxMs',
        'poorLatencyMs',
        'poorLatencyThresholdMs'
      ]) ?? 900
  };
}

function normalizeConnectionInfo(value: unknown): RealtimeConnectionInfo {
  const payload = unwrapApiValue(value);
  const record = isRecord(payload) ? payload : {};

  return {
    connectionId: readStringValue(record, ['connectionId']),
    tenantId: readStringValue(record, ['tenantId']),
    userId: readStringValue(record, ['userId']),
    serverTime: readStringValue(record, ['serverTime', 'serverNow']),
    hubUrl: normalizeHubUrl(
      readStringValue(record, ['hubUrl', 'hubPath', 'notificationsHubUrl'])
    ),
    pingIntervalMs:
      readNumberValue(record, ['pingIntervalMs', 'pingInterval']) ?? 20000,
    latencyThresholds: normalizeLatencyThresholds(record)
  };
}

export async function getRealtimeConnectionInfo(accessToken: string) {
  const response = await axios.get(
    `${normalizeApiBaseUrl()}/api/v1/realtime/connection-info`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...getTenantRequestHeaders()
      }
    }
  );

  return normalizeConnectionInfo(response.data);
}

export async function searchRealtimeAppNotifications(accessToken: string) {
  const page = await searchRealtimeAppNotificationsPage(accessToken, {
    pageNumber: 1,
    pageSize: 50
  });

  return page.data;
}

export async function searchRealtimeAppNotificationsPage(
  accessToken: string,
  options: {
    pageNumber: number;
    pageSize: number;
    isRead?: boolean;
  }
): Promise<RealtimeAppNotificationsPage> {
  const response = await axios.post(
    `${normalizeApiBaseUrl()}/api/v1/app-notifications/search`,
    {
      pageNumber: options.pageNumber,
      pageSize: options.pageSize,
      isRead: options.isRead
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...getTenantRequestHeaders()
      }
    }
  );
  const envelope = isRecord(response.data) ? response.data : {};
  const record = readRecordValue(envelope, ['value', 'result']) ?? envelope;
  const payload = unwrapApiValue(record);
  const data = Array.isArray(payload)
    ? payload
    : isRecord(payload) && Array.isArray(payload.data)
      ? payload.data
      : [];

  return {
    data,
    totalCount:
      readNumberValue(record, ['totalCount', 'total', 'count']) ?? data.length,
    currentPage:
      readNumberValue(record, ['currentPage', 'pageNumber', 'page']) ??
      options.pageNumber,
    pageSize: readNumberValue(record, ['pageSize', 'size']) ?? options.pageSize
  };
}

export async function getRealtimeNotificationPreferences(accessToken: string) {
  const response = await axios.get(
    `${normalizeApiBaseUrl()}/api/personal/profile-settings`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...getTenantRequestHeaders()
      }
    }
  );
  const payload = unwrapApiValue(response.data);
  const settings = isRecord(payload) ? payload : {};
  const readPreference = (key: keyof RealtimeNotificationPreferences) =>
    typeof settings[key] === 'boolean'
      ? (settings[key] as boolean)
      : defaultRealtimeNotificationPreferences[key];

  return {
    pushNotifications: readPreference('pushNotifications'),
    newOrderNotification: readPreference('newOrderNotification'),
    commentNotification: readPreference('commentNotification'),
    statusNotification: readPreference('statusNotification'),
    reminderNotification: readPreference('reminderNotification')
  };
}

export async function markRealtimeAppNotificationAsRead(
  accessToken: string,
  notificationId: string
) {
  if (
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      notificationId
    )
  ) {
    return;
  }

  await axios.put(
    `${normalizeApiBaseUrl()}/api/v1/app-notifications/${encodeURIComponent(notificationId)}/read`,
    undefined,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...getTenantRequestHeaders()
      }
    }
  );
}

export function createNotificationHubConnection({
  accessTokenFactory,
  connectionInfo
}: {
  accessTokenFactory: () => string;
  connectionInfo: RealtimeConnectionInfo;
}): HubConnection {
  const options: IHttpConnectionOptions = {
    accessTokenFactory,
    headers: getSignalRTenantHeaders()
  };

  return new HubConnectionBuilder()
    .withUrl(
      connectionInfo.hubUrl ?? `${normalizeApiBaseUrl()}/notifications`,
      options
    )
    .withAutomaticReconnect([0, 2000, 10000, 30000])
    .configureLogging(LogLevel.Warning)
    .build();
}
